import React, {createContext, useContext, useEffect, useState} from 'react'

export type ThemeModeType = 'dark' | 'light' | 'system'
const systemMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

type ThemeModeContextType = {
  mode: ThemeModeType
  menuMode: ThemeModeType
  updateMode: (_mode: ThemeModeType) => void
  updateMenuMode: (_mode: ThemeModeType) => void
}

const themeModeLSKey = 'kt_theme_mode_value'
const themeMenuModeLSKey = 'kt_theme_mode_menu'

const getThemeModeFromLocalStorage = (lsKey: string): ThemeModeType => {
  if (localStorage) {
    const saved = localStorage.getItem(lsKey)
    if (saved === 'light' || saved === 'dark') return saved
  }
  return 'dark'
}

// Apply CSS custom properties via JavaScript so they always work,
// independent of which CSS bundle version the browser has cached.
const applyThemeVars = (mode: ThemeModeType) => {
  const r = document.documentElement
  if (mode === 'dark') {
    r.style.setProperty('--page-bg', '#0D0E12')
    r.style.setProperty('--card-bg', '#151720')
    r.style.setProperty('--card-border', 'rgba(255,255,255,0.10)')
    r.style.setProperty('--elevated-bg', '#1C1F2A')
    r.style.setProperty('--input-bg', '#0d1117')
    r.style.setProperty('--input-border', 'rgba(255,255,255,0.08)')
    r.style.setProperty('--text-primary', '#f1f5f9')
    r.style.setProperty('--text-secondary', '#cbd5e1')
    r.style.setProperty('--text-muted', '#94a3b8')
    r.style.setProperty('--text-faint', '#64748b')
    r.style.setProperty('--divider', 'rgba(255,255,255,0.08)')
    r.style.setProperty('--chip-active-bg', 'rgba(255,255,255,0.08)')
    r.style.setProperty('--chip-active-text', '#f1f5f9')
    r.style.setProperty('--chip-inactive-bg', 'transparent')
    r.style.setProperty('--chip-inactive-border', 'rgba(255,255,255,0.07)')
  } else {
    r.style.setProperty('--page-bg', '#f5f8fa')
    r.style.setProperty('--card-bg', '#ffffff')
    r.style.setProperty('--card-border', 'rgba(0,0,0,0.08)')
    r.style.setProperty('--elevated-bg', '#f1f5f9')
    r.style.setProperty('--input-bg', '#ffffff')
    r.style.setProperty('--input-border', 'rgba(0,0,0,0.12)')
    r.style.setProperty('--text-primary', '#0f172a')
    r.style.setProperty('--text-secondary', '#374151')
    r.style.setProperty('--text-muted', '#64748b')
    r.style.setProperty('--text-faint', '#94a3b8')
    r.style.setProperty('--divider', 'rgba(0,0,0,0.06)')
    r.style.setProperty('--chip-active-bg', 'rgba(15,23,42,0.08)')
    r.style.setProperty('--chip-active-text', '#0f172a')
    r.style.setProperty('--chip-inactive-bg', 'transparent')
    r.style.setProperty('--chip-inactive-border', 'rgba(0,0,0,0.10)')
  }
}

const defaultThemeMode: ThemeModeContextType = {
  mode: getThemeModeFromLocalStorage(themeModeLSKey),
  menuMode: getThemeModeFromLocalStorage(themeMenuModeLSKey),
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultThemeMode.mode,
  menuMode: defaultThemeMode.menuMode,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
})

const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({children}: {children: React.ReactNode}) => {
  const [mode, setMode] = useState<ThemeModeType>(defaultThemeMode.mode)
  const [menuMode, setMenuMode] = useState<ThemeModeType>(defaultThemeMode.menuMode)

  const updateMode = (_mode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    const updatedMode = _mode === 'system' ? systemMode : _mode
    setMode(updatedMode)
    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(themeModeLSKey, updatedMode)
    }
    document.documentElement.setAttribute('data-theme', updatedMode)
    document.body.setAttribute('data-bs-theme', updatedMode)
    applyThemeVars(updatedMode)
  }

  const updateMenuMode = (_menuMode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    setMenuMode(_menuMode)
    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(themeMenuModeLSKey, _menuMode)
    }
  }

  useEffect(() => {
    updateMode(mode, false)
    updateMenuMode(menuMode, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ThemeModeContext.Provider value={{mode, menuMode, updateMode, updateMenuMode}}>
      {children}
    </ThemeModeContext.Provider>
  )
}

export {ThemeModeProvider, useThemeMode, systemMode}
